/**
 * Purpose:
 *   Render the collapsed desktop menu popover for menu items in the collapsed sidebar.
 *
 * Notes:
 *   - Uses a portal so the floating panel is not clipped by the sidebar container
 *   - Reuses the existing submenu link renderer to keep navigation behavior consistent
 *   - Supports both single-link items and category items with nested routes
 */

'use client';

import { createPortal } from 'react-dom';
import { cx } from '@/util/functions/cx';
import { MenuItemSubmenuItem } from './MenuItemSubmenuItem';
import {
  MENU_COLLAPSED_POPOVER,
  MENU_COLLAPSED_POPOVER_ARROW,
  MENU_COLLAPSED_POPOVER_ARROW_INNER,
  MENU_COLLAPSED_POPOVER_VISIBLE,
  MENU_COLLAPSED_POPOVER_HEADER,
  MENU_COLLAPSED_POPOVER_LIST,
} from './Menu.styles';
import type { MenuItemConfig } from '@/types/menu';

export interface ICollapsedMenuItemPopoverProps {
  item: MenuItemConfig;
  isOpen: boolean;
  canPortal: boolean;
  position: {
    left: number;
    top: number;
    arrowOffsetTop: number;
  } | null;
  popoverRef: React.RefObject<HTMLDivElement | null>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: (event: React.FocusEvent<HTMLElement>) => void;
  isChildActive: (item: MenuItemConfig) => boolean;
  onNavigate: (href: string) => void;
  onLinkClick?: () => void;
}

export function CollapsedMenuItemPopover({
  item,
  isOpen,
  canPortal,
  position,
  popoverRef,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  isChildActive,
  onNavigate,
  onLinkClick,
}: ICollapsedMenuItemPopoverProps) {
  if (!canPortal || !isOpen) return null;

  const popoverItems = item.children?.length ? item.children : item.href ? [item] : [];

  if (popoverItems.length === 0) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className={cx(
        MENU_COLLAPSED_POPOVER,
        MENU_COLLAPSED_POPOVER_VISIBLE
      )}
      data-testid={`collapsed-menu-popover-${item.id}`}
      style={position ? { left: position.left, top: position.top } : { left: -9999, top: -9999 }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      {position ? (
        <span
          className={MENU_COLLAPSED_POPOVER_ARROW}
          style={{ top: position.arrowOffsetTop }}
          aria-hidden="true"
        >
          <span className={MENU_COLLAPSED_POPOVER_ARROW_INNER} />
        </span>
      ) : null}
      <div className={MENU_COLLAPSED_POPOVER_HEADER}>{item.label}</div>
      <div className={MENU_COLLAPSED_POPOVER_LIST}>
        {popoverItems.map((child) => (
          <MenuItemSubmenuItem
            key={child.id}
            child={child}
            isActive={isChildActive(child)}
            isFocusable={isOpen}
            onNavigate={onNavigate}
            onLinkClick={onLinkClick}
          />
        ))}
      </div>
    </div>,
    document.body
  );
}
